import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Event, Router, Scroll } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Smooth scrolling for route changes, back/forward restoration, and hash anchors.
 * Replaces Angular's default instant ViewportScroller behavior.
 */
@Injectable({ providedIn: 'root' })
export class SmoothScrollService {
  private readonly router = inject(Router);
  private readonly viewport = inject(ViewportScroller);
  private readonly platformId = inject(PLATFORM_ID);
  private started = false;

  /** Call once from app bootstrap (browser only). */
  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.started = true;

    this.viewport.setOffset([0, this.headerOffset()]);

    this.router.events
      .pipe(filter((e: Event): e is Scroll => e instanceof Scroll))
      .subscribe((e) => this.onScrollEvent(e));
  }

  /** Smooth scroll to page top (or instant when reduced motion is preferred). */
  toTop(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: this.behavior() });
  }

  /** Smooth scroll an element into view by id (without leading #). */
  toAnchor(anchor: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const el = document.getElementById(anchor);
    if (!el) {
      return;
    }
    const top = el.getBoundingClientRect().top + window.scrollY - this.headerOffset();
    window.scrollTo({ top, left: 0, behavior: this.behavior() });
  }

  private onScrollEvent(e: Scroll): void {
    // Defer until after the new view paints so layout height is correct.
    requestAnimationFrame(() => {
      if (e.position) {
        window.scrollTo({
          left: e.position[0],
          top: e.position[1],
          behavior: this.behavior(),
        });
      } else if (e.anchor) {
        this.toAnchor(e.anchor);
      } else {
        this.toTop();
      }
    });
  }

  private behavior(): ScrollBehavior {
    return this.prefersReducedMotion() ? 'auto' : 'smooth';
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Sticky header clearance for in-page anchors. */
  private headerOffset(): number {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim();
    const px = Number.parseFloat(raw);
    return Number.isFinite(px) ? Math.round(px) : 76;
  }
}
